/**
 * Write Clawnify's always-on "rules" block into an agent's main rules
 * file (CLAUDE.md, AGENTS.md, …). This is the always-in-context layer —
 * distinct from `clawnify skills add`, which installs on-demand skills.
 *
 * The block is marker-delimited and version-pinned so reruns replace it
 * in place rather than appending duplicates. Like skills, the content is
 * bundled in the CLI and only changes when the CLI version changes —
 * re-run `ai-files install` after `npm i -g clawnify@latest` to upgrade.
 */

import { existsSync, mkdirSync, readFileSync, unlinkSync, writeFileSync } from "node:fs";
import { dirname, join } from "node:path";
import { CLI_VERSION, RULES_CONTENT } from "../skills/content.gen.js";
import type { Agent } from "../skills/detect.js";

/** Where each agent reads its always-on instructions. */
export const RULES_FILE: Record<Agent, string> = {
  claude: "CLAUDE.md",
  cursor: ".cursorrules",
  openclaw: "AGENTS.md",
  codex: "AGENTS.md",
  aider: "CONVENTIONS.md",
};

const START = "<!-- clawnify:rules:start -->";
const END = "<!-- clawnify:rules:end -->";
const VERSION_RE = /<!--\s*clawnify:rules\s+v([\d.]+)\s*-->/;

function block(): string {
  return `${START}
<!-- clawnify:rules v${CLI_VERSION} -->
${RULES_CONTENT.trimEnd()}
${END}
`;
}

export type RulesStatus =
  | { status: "installed"; file: string }
  | { status: "updated"; file: string; previousVersion: string }
  | { status: "skipped"; file: string; reason: string };

/**
 * Insert or refresh the rules block in `<cwd>/<file>`. Creates the file
 * if it doesn't exist.
 */
export function writeRules(cwd: string, file: string): RulesStatus {
  const filePath = join(cwd, file);
  const section = block();
  const sectionRe = new RegExp(`${escapeRe(START)}[\\s\\S]*?${escapeRe(END)}\\n?`);

  if (existsSync(filePath)) {
    const existing = readFileSync(filePath, "utf8");
    const match = existing.match(sectionRe);
    if (match) {
      const versionMatch = match[0].match(VERSION_RE);
      const previousVersion = versionMatch ? versionMatch[1] : null;
      if (previousVersion === CLI_VERSION) {
        return { status: "skipped", file: filePath, reason: `already at v${CLI_VERSION}` };
      }
      writeFileSync(filePath, existing.replace(sectionRe, section));
      return previousVersion
        ? { status: "updated", file: filePath, previousVersion }
        : { status: "installed", file: filePath };
    }
    // File exists but has no block — append after a blank line.
    const prefix = existing.endsWith("\n") ? "\n" : "\n\n";
    writeFileSync(filePath, existing + prefix + section);
    return { status: "installed", file: filePath };
  }

  mkdirSync(dirname(filePath), { recursive: true });
  writeFileSync(filePath, section);
  return { status: "installed", file: filePath };
}

/** Strip the rules block from `<cwd>/<file>`. Returns true if removed. */
export function removeRules(cwd: string, file: string): boolean {
  const filePath = join(cwd, file);
  if (!existsSync(filePath)) return false;
  const existing = readFileSync(filePath, "utf8");
  const sectionRe = new RegExp(`\\n?${escapeRe(START)}[\\s\\S]*?${escapeRe(END)}\\n?`);
  if (!sectionRe.test(existing)) return false;
  const next = existing.replace(sectionRe, "\n").replace(/^\n+/, "").trimEnd();
  if (next) {
    writeFileSync(filePath, next + "\n");
  } else {
    // Our block was the file's only content — don't leave a 0-byte file.
    unlinkSync(filePath);
  }
  return true;
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
